import { memo, useEffect, useMemo, type ReactNode } from 'react'
import { logAmplitudeEvent } from '../../../../../utils'
import { useActions } from '../../../../../providers'
import { useAtom } from 'jotai'
import {
  contextSourcesAtom,
  useCurrentWindowToSelection,
  useResetChatContext,
  useWindowSelection,
} from '../../../../../store/atoms'
import { AudioMenuItem } from './audio-menu'
import { FadingScrollable } from '../../../../elements/FadingScrollable'
import { type FocusedWindow } from '@highlight/common'
import { ReorderingList } from '../../../../elements/ReorderingList'
import { useAddWindow } from '../../../../../hooks/useAddWindow'
import {
  useAttachLocalFiles,
  useAttachLocalFolder,
  useAttachmentStore,
  useFileAttachSupport,
  useIsMaxAttachmentsReached,
} from '../../../../../store'
import { isElectron } from '../../../../../utils/runtimeUtils'
import { WindowSelectionMenu } from '../../../attachments/screenshot-picker'
import { useTrackAddAttachment } from '../../../../../hooks/attachments-hooks'
import { BrowserTabsMenuItem } from './browser-tabs-menu'
import { useUniqueWindowSources } from '../../../../../hooks/useUniqueWindowSources'
import { Mic, Check, ClipboardList, FileUp, Folder, Zap, RotateCw, AppWindow } from 'lucide-react'
import { ClipboardMenuItem } from './clipboard-menu'
import { SkillsMenuItem } from './skills-menu'
import { useLdFlag } from '../../../../../hooks'
import { FEATURE_FLAGS } from '@highlight/analytics'
import { useFlags } from '@highlight/analytics/react'
import { twMerge } from 'tailwind-merge'
import { Badge, Menu, Tooltip, listItemCheck, listItemTrailing } from '@highlight/ui'

export type AttachmentMenuItemType = {
  id: string
  icon?: React.ReactElement
  label?: string | React.ReactElement
  render?: (props: AttachmentMenuItemType) => React.ReactElement
  isMenu?: boolean
  selectable?: boolean
  selected?: boolean
  animateShift?: boolean
  isAttachment?: boolean
  onClick?: (e: React.MouseEvent) => void
  className?: string
  disabled?: boolean
  /** A second line under the label: a timestamp, a source. */
  description?: ReactNode
  /** A shortcut or count at the row's end. */
  trailing?: ReactNode
  window?: FocusedWindow
}

/**
 * A row of the attachments menu. A Menu.Item that stays open on click, so several things can be
 * attached in one visit; a selectable row shows the shared trailing check while attached.
 */
export const AttachmentMenuItem = memo((item: AttachmentMenuItemType) => {
  const isMaxAttachmentsReached = useIsMaxAttachmentsReached()
  if (typeof item.render === 'function') {
    return item.render(item)
  }
  return (
    <Menu.Item
      closeOnClick={false}
      disabled={item.disabled || (isMaxAttachmentsReached && item.isAttachment !== false && !item.selected)}
      className={twMerge('max-w-72', item.className)}
      onClick={item.onClick}
    >
      {item.icon}
      <Menu.ItemText description={item.description}>{item.label}</Menu.ItemText>
      {item.trailing && <span className={listItemTrailing}>{item.trailing}</span>}
      {item.selectable && item.selected && (
        <Check data-slot="list-item-check" aria-label="Attached" className={listItemCheck} />
      )}
    </Menu.Item>
  )
})
AttachmentMenuItem.displayName = 'AttachmentMenuItem'

/** Marks a menu row as newly shipped; hovering it says what changed. */
const NewBadge = ({ tooltip }: { tooltip: string }) => (
  <Tooltip>
    {/* Badge can't take the trigger's ref, so a span anchors the tooltip. */}
    <Tooltip.Trigger render={<span className="inline-flex" />}>
      <Badge tone="brand" size="xs">
        New
      </Badge>
    </Tooltip.Trigger>
    <Tooltip.Content side="right">{tooltip}</Tooltip.Content>
  </Tooltip>
)

/**
 * A row that opens a submenu: Base UI's submenu, so it opens on hover with a safe triangle toward
 * the list, and arrow keys move into it.
 */
export const AttachmentSubmenu = ({ item, children }: { item: AttachmentMenuItemType; children: ReactNode }) => (
  <Menu.SubmenuRoot>
    <Menu.SubmenuTrigger className="max-w-72">
      {item.icon}
      <Menu.ItemText>{item.label}</Menu.ItemText>
    </Menu.SubmenuTrigger>
    <Menu.Content className="max-w-72">
      <FadingScrollable className="flex max-h-60 flex-col gap-px">{children}</FadingScrollable>
    </Menu.Content>
  </Menu.SubmenuRoot>
)

export const AttachmentMenuItems = memo(() => {
  const { getScreenshotSources } = useActions()
  const [sources, setSources] = useAtom(contextSourcesAtom)
  const { removeSelectedWindow } = useWindowSelection()
  const currentWindow = useCurrentWindowToSelection()
  const resetContext = useResetChatContext()
  const onAddWindow = useAddWindow()
  const { addScreenshotAttachment } = useAttachmentStore()
  const trackAddAttachment = useTrackAddAttachment()
  const showClipboardHistory = useLdFlag('clipboard-history-attachments', false)
  const showBrowserTabs = useLdFlag('browser-tabs-attachments', false)
  const flags = useFlags()
  const showLocalFiles =
    !!useFileAttachSupport().data?.isAvailable && flags.isEnabled(FEATURE_FLAGS.ATTACH_LOCAL_FILES, false)
  const showNewBadges = showLocalFiles && flags.isEnabled(FEATURE_FLAGS.ATTACH_LOCAL_FILES_NEW_BADGE, false)
  const attachLocalFiles = useAttachLocalFiles()
  const attachLocalFolder = useAttachLocalFolder()

  const windows = useUniqueWindowSources()

  const fetchSources = async () => {
    const sources = await getScreenshotSources()
    setSources(sources)
  }

  const attachments = useMemo(() => {
    return [
      ...(showLocalFiles
        ? [
            {
              id: 'attach-file',
              label: 'Attach File',
              icon: <FileUp />,
              trailing: showNewBadges && (
                <NewBadge tooltip="You can now attach any file to Highlight, not just images." />
              ),
              onClick: () => {
                void attachLocalFiles()
                window.ampltd.trackEvent('Click Attachment Menu Item', { label: 'Attach File' })
              },
            },
            {
              id: 'attach-folder',
              label: 'Attach Folder',
              icon: <Folder />,
              trailing: showNewBadges && (
                <NewBadge tooltip="Highlight can read and search every file in a folder, but it can't make any changes without your approval." />
              ),
              onClick: () => {
                void attachLocalFolder()
                window.ampltd.trackEvent('Click Attachment Menu Item', { label: 'Attach Folder' })
              },
            },
          ]
        : [
            {
              id: 'upload',
              label: 'Upload File',
              icon: <FileUp />,
              onClick: () => {
                const el = document.getElementById('file-attachment-handler')
                if (!el) {
                  return
                }
                el.click()
                window.ampltd.trackEvent('Click Attachment Menu Item', {
                  label: 'Upload File',
                })
              },
            },
          ]),
      {
        id: 'audio-notes',
        label: 'Audio Notes',
        icon: <Mic />,
        isMenu: true,
        isAttachment: false,
        render: (item: AttachmentMenuItemType) => <AudioMenuItem {...item} />,
      },
      {
        id: 'actions',
        label: 'Actions',
        icon: <Zap />,
        isMenu: true,
        isAttachment: false,
        render: (item: AttachmentMenuItemType) => <SkillsMenuItem {...item} />,
      },
      ...(showBrowserTabs
        ? [
            {
              id: 'browser-tabs',
              label: 'Browser Tabs',
              // kinda hate this but temporary
              icon: <AppWindow />,
              isMenu: true,
              isAttachment: false,
              render: (item: AttachmentMenuItemType) => <BrowserTabsMenuItem {...item} />,
            },
          ]
        : []),
      ...(showClipboardHistory
        ? [
            {
              id: 'clipboard-history',
              label: 'Clipboard History',
              icon: <ClipboardList />,
              isMenu: true,
              isAttachment: false,
              render: (item: AttachmentMenuItemType) => <ClipboardMenuItem {...item} />,
            },
          ]
        : []),
    ]
  }, [showClipboardHistory, showBrowserTabs, showLocalFiles, showNewBadges, attachLocalFiles, attachLocalFolder])

  const options = useMemo(() => {
    return [
      {
        id: 'remove-all',
        icon: <RotateCw />,
        label: 'Clear Attachments',
        trailing: `${process.platform === 'darwin' ? '⌘' : 'Ctrl'} Shift Del`,
        isAttachment: false,
        onClick: () => {
          resetContext()
          window.ampltd.trackEvent('Click Attachment Menu Item', {
            label: 'Clear',
          })
        },
      },
    ]
  }, [resetContext])

  useEffect(() => {
    fetchSources()
  }, [currentWindow?.windowTitle])

  return (
    <>
      {attachments.map((item) => (
        <AttachmentMenuItem {...item} key={item.id} />
      ))}

      <Menu.Separator />

      {!isElectron && <WindowSelectionMenu className={'max-h-40'} isSearchable={false} />}

      {isElectron && (
        <FadingScrollable className="flex max-h-44 flex-col gap-px">
          <ReorderingList className="w-full gap-px">
            {windows.map((item) => (
              <AttachmentMenuItem
                {...item}
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation()
                  if (item.selected) {
                    removeSelectedWindow(item.window!)
                    logAmplitudeEvent('Remove Window Context', {
                      appName: item.window!.appName,
                      focusedWindow:
                        item.window!.appName === currentWindow?.appName &&
                        item.window!.pid === currentWindow?.pid &&
                        item.window!.windowTitle === currentWindow?.windowTitle,
                      trigger: 'Attachments Menu',
                    })
                    return
                  }
                  onAddWindow(item.window!)
                  logAmplitudeEvent('Add Window Context', {
                    appName: item.window!.appName,
                    focusedWindow:
                      item.window!.appName === currentWindow?.appName &&
                      item.window!.pid === currentWindow?.pid &&
                      item.window!.windowTitle === currentWindow?.windowTitle,
                    trigger: 'Attachments Menu',
                  })
                }}
              />
            ))}
          </ReorderingList>
          {sources.displays.map((display, index) => (
            <AttachmentMenuItem
              key={`display-${index}`}
              id={`display-${index}`}
              label={`Display ${index + 1}`}
              icon={<img src={display.thumbnail} className={'h-4 w-4 rounded-md'} />}
              selectable={false}
              onClick={() => {
                addScreenshotAttachment(display.thumbnail, `Display ${index + 1}`)
                trackAddAttachment({ type: 'image (display)', trigger: 'Plus Button' })
              }}
            />
          ))}
        </FadingScrollable>
      )}

      <Menu.Separator />

      {options.map((item) => (
        <AttachmentMenuItem {...item} key={item.id} />
      ))}
    </>
  )
})
AttachmentMenuItems.displayName = 'AttachmentMenuItems'
